// =============================================================================
// ResourceDocumentsPanel.jsx — the Documents tab of Manage → Resources Management
// =============================================================================
// The Resources document store (db-138): upload a form/template, give it a display
// NAME, then Copy link and paste that link into a General / Invoicing / Responders
// page with the editor's existing Link button.
//
// The link is `/resource-doc/<row id>` — built from the row id, NOT the storage
// path — so renaming a document or replacing its file leaves every link already
// pasted into a page working. That property is the whole reason this screen has a
// "Replace file" action rather than making you delete and re-upload.
//
// Documents are shared across all three sections on purpose: one library, linkable
// from anywhere.
// =============================================================================

import React from "react";
import {
  createResourceDocument,
  deactivateResourceDocument,
  listResourceDocuments,
  updateResourceDocument,
} from "../../data/resourceDocuments";
import { getResourceDocSignedUrl, uploadResourceDoc } from "../../data/storage";
import { confirmDialog } from "../../services/dialogService";
import {
  docTypeLabel,
  downloadFileName,
  formatFileSize,
  isInlineViewable,
  RESOURCE_DOC_MAX_LABEL,
  resourceDocLink,
  resourceDocSizeError,
  suggestDocName,
} from "../../services/resourceDocs";
import { logAudit } from "../../utils/auditLog";
import { Btn } from "../../utils/ui.jsx";

// Mirrors the bucket's allowed_mime_types (db-138). The bucket enforces this
// server-side; this just keeps the file picker tidy and fails fast.
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx";

// Copy to the clipboard, falling back to the legacy execCommand path for browsers
// or contexts where the async Clipboard API isn't available (it requires a secure
// context). Returns true when the text made it across.
async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_e) {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_e) {
    return false;
  }
}

// A new document id. The id is generated HERE, before the upload, because the
// storage object is stored under "<id>/…" — see uploadResourceDoc.
function newDocId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  // Fallback for environments without randomUUID (older jsdom, insecure context).
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const labelCls = "text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1";
const inputCls =
  "py-[8px] px-[10px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white";
const linkBtnCls =
  "bg-transparent border-none cursor-pointer text-[12px] text-zephyr-teal-dark px-1";

export function ResourceDocumentsPanel({ user }) {
  const [rows, setRows] = React.useState(null);
  const [msg, setMsg] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  // Add form
  const [file, setFile] = React.useState(null);
  const [name, setName] = React.useState("");
  // Inline rename: the row being renamed + its draft name.
  const [renameId, setRenameId] = React.useState(null);
  const [renameDraft, setRenameDraft] = React.useState("");
  // Which row just had its link copied (a transient "Copied ✓" tick).
  const [copiedId, setCopiedId] = React.useState(null);
  const addInputRef = React.useRef(null);
  const replaceInputRef = React.useRef(null);
  const replaceForId = React.useRef(null);

  const load = React.useCallback(function () {
    return listResourceDocuments().then(function (r) {
      setRows(r || []);
    });
  }, []);

  React.useEffect(
    function () {
      load();
    },
    [load]
  );

  function onPickFile(e) {
    const f = e.target.files?.[0] || null;
    // Fail fast on an oversized file. The bucket enforces the cap server-side;
    // catching it here just saves a pointless upload and a raw storage error.
    const tooBig = resourceDocSizeError(f);
    if (tooBig) {
      setFile(null);
      setMsg(tooBig);
      if (addInputRef.current) addInputRef.current.value = "";
      return;
    }
    setFile(f);
    setMsg("");
    // Pre-fill the display name from the file name, but leave it editable — the
    // name is what people read in a page, not the file name.
    if (f && !name.trim()) setName(suggestDocName(f.name));
  }

  async function addDocument() {
    if (!file || !name.trim() || busy) return;
    setBusy(true);
    setMsg("");
    const id = newDocId();
    // Upload FIRST: it's the step most likely to fail (the bucket enforces the
    // size + MIME whitelist), and a failed upload should leave no row behind.
    const up = await uploadResourceDoc(id, file);
    if (up.error) {
      setBusy(false);
      setMsg(`Upload failed: ${up.error.message}`);
      return;
    }
    const { error } = await createResourceDocument({
      id,
      name: name.trim(),
      file_path: up.path,
      mime_type: file.type || null,
      size_bytes: file.size ?? null,
      uploaded_by: user?.profileId || user?.id || null,
    });
    setBusy(false);
    if (error) {
      setMsg(`Save failed: ${error.message}`);
      return;
    }
    logAudit("resource_document_uploaded", { id, name: name.trim(), by: user?.email || "" });
    setFile(null);
    setName("");
    if (addInputRef.current) addInputRef.current.value = "";
    setMsg("Document added.");
    load();
  }

  async function copyLink(row) {
    const link = resourceDocLink(window.location.origin, row.id);
    const ok = await copyToClipboard(link);
    if (!ok) {
      setMsg(`Couldn't copy automatically — the link is ${link}`);
      return;
    }
    setCopiedId(row.id);
    setMsg("");
    window.setTimeout(function () {
      setCopiedId(function (cur) {
        return cur === row.id ? null : cur;
      });
    }, 2000);
  }

  function startRename(row) {
    setRenameId(row.id);
    setRenameDraft(row.name || "");
  }

  async function saveRename() {
    const trimmed = renameDraft.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    // Only `name` is patched — the id (the link) and file_path are untouched.
    const { error } = await updateResourceDocument(renameId, { name: trimmed });
    setBusy(false);
    if (error) {
      setMsg(`Rename failed: ${error.message}`);
      return;
    }
    logAudit("resource_document_renamed", { id: renameId, name: trimmed, by: user?.email || "" });
    setRenameId(null);
    setRenameDraft("");
    setMsg("Renamed.");
    load();
  }

  function pickReplacement(row) {
    replaceForId.current = row.id;
    if (replaceInputRef.current) {
      replaceInputRef.current.value = "";
      replaceInputRef.current.click();
    }
  }

  async function onPickReplacement(e) {
    const f = e.target.files?.[0] || null;
    const id = replaceForId.current;
    if (!f || !id) return;
    // Same cap as the Add path — a Replace must not be able to slip a bigger
    // file past the check the Add form applies.
    const tooBig = resourceDocSizeError(f);
    if (tooBig) {
      setMsg(tooBig);
      return;
    }
    setBusy(true);
    setMsg("");
    const up = await uploadResourceDoc(id, f);
    if (up.error) {
      setBusy(false);
      setMsg(`Upload failed: ${up.error.message}`);
      return;
    }
    // The link does NOT change — only the stored object behind it.
    const { error } = await updateResourceDocument(id, {
      file_path: up.path,
      mime_type: f.type || null,
      size_bytes: f.size ?? null,
    });
    setBusy(false);
    if (error) {
      setMsg(`Save failed: ${error.message}`);
      return;
    }
    logAudit("resource_document_replaced", { id, file: f.name, by: user?.email || "" });
    setMsg("File replaced — existing links still work.");
    load();
  }

  async function openDocument(row) {
    // Non-PDFs are signed with a download name, or the browser tries to render
    // them inline and fails ("Couldn't load plugin" on a .docx).
    const inline = isInlineViewable(row.mime_type);
    const url = await getResourceDocSignedUrl(
      row.file_path,
      300,
      inline ? undefined : downloadFileName(row.name, row.file_path)
    );
    if (!url) {
      setMsg("Could not open that file.");
      return;
    }
    window.open(url, "_blank", "noopener");
  }

  async function removeDocument(row) {
    const ok = await confirmDialog({
      title: "Remove document",
      message: `Remove “${row.name}”? Any link to it already placed in a Resources page will stop working and show a "no longer available" message.`,
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    const { error } = await deactivateResourceDocument(row.id);
    setBusy(false);
    if (error) {
      setMsg(`Remove failed: ${error.message}`);
      return;
    }
    logAudit("resource_document_deactivated", {
      id: row.id,
      name: row.name,
      by: user?.email || "",
    });
    setMsg("Document removed.");
    load();
  }

  const failed = /failed|Couldn't|Could not/.test(msg);

  return (
    <div>
      {/* Add a document */}
      <div className="border border-zephyr-border rounded-[10px] p-5 bg-white mb-4">
        <div className="text-[15px] font-bold text-zephyr-teal-dark mb-1">Add a document</div>
        <div className="text-[12px] text-zephyr-muted mb-3">
          PDF or Office files up to {RESOURCE_DOC_MAX_LABEL}. Once added, use{" "}
          <strong>Copy link</strong> and paste the link into a page with the editor’s link button.
        </div>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <span className={labelCls}>File</span>
            {/* Native file inputs can't be styled — hidden input + a link-styled
                label (bold + underlined so it's obviously clickable, John), the
                same pattern as the Import screens. The chosen file name is shown
                alongside, since hiding the input also hides "No file chosen". */}
            <div className="flex items-center gap-2 py-[8px]">
              <label
                htmlFor="rd-file"
                className="cursor-pointer font-bold underline text-zephyr-teal-dark text-[13px]"
              >
                📄 Choose a file…
              </label>
              <span className="text-[13px] text-zephyr-muted">
                {file ? file.name : "No file chosen"}
              </span>
            </div>
            <input
              id="rd-file"
              ref={addInputRef}
              type="file"
              accept={ACCEPT}
              aria-label="Document file"
              onChange={onPickFile}
              className="hidden"
            />
          </div>
          <div>
            <label htmlFor="rd-name" className={labelCls}>
              Document name
            </label>
            <input
              id="rd-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Uniform Request Form"
              className={`${inputCls} w-[320px]`}
            />
          </div>
          <Btn onClick={addDocument} disabled={busy || !file || !name.trim()}>
            {busy ? "Working…" : "Add document"}
          </Btn>
        </div>
      </div>

      {msg && (
        <div
          className={`mb-3 text-[13px] font-semibold ${failed ? "text-[#C62828]" : "text-[#2E7D32]"}`}
        >
          {msg}
        </div>
      )}

      {/* Hidden input reused by every row's Replace file action. */}
      <input
        ref={replaceInputRef}
        type="file"
        accept={ACCEPT}
        onChange={onPickReplacement}
        className="hidden"
      />

      {rows === null ? (
        <div className="text-zephyr-muted p-5">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-zephyr-border rounded-[10px] p-10 text-center text-zephyr-muted text-[14px]">
          No documents yet.
        </div>
      ) : (
        <div className="border border-zephyr-border rounded-[10px] bg-white overflow-hidden">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="bg-zephyr-off-white text-left">
                <th className="py-2 px-3 font-bold text-zephyr-muted">Name</th>
                <th className="py-2 px-3 font-bold text-zephyr-muted w-[90px]">Type</th>
                <th className="py-2 px-3 font-bold text-zephyr-muted w-[90px]">Size</th>
                <th className="py-2 px-3 font-bold text-zephyr-muted w-[120px]">Added</th>
                <th className="py-2 px-3 font-bold text-zephyr-muted w-[330px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(function (row) {
                const renaming = renameId === row.id;
                return (
                  <tr key={row.id} className="border-t border-zephyr-border align-middle">
                    <td className="py-2 px-3">
                      {renaming ? (
                        <div className="flex gap-2 items-center">
                          <input
                            type="text"
                            value={renameDraft}
                            onChange={(e) => setRenameDraft(e.target.value)}
                            aria-label="Rename document"
                            className={`${inputCls} w-[280px]`}
                          />
                          <button
                            type="button"
                            onClick={saveRename}
                            disabled={busy || !renameDraft.trim()}
                            className={linkBtnCls}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setRenameId(null)}
                            className={linkBtnCls}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openDocument(row)}
                          title="Open this document"
                          className="bg-transparent border-none cursor-pointer p-0 text-[13px] text-zephyr-teal-dark font-semibold text-left underline"
                        >
                          {row.name}
                        </button>
                      )}
                    </td>
                    <td className="py-2 px-3 text-zephyr-muted">
                      {docTypeLabel(row.mime_type, row.file_path)}
                    </td>
                    <td className="py-2 px-3 text-zephyr-muted">
                      {formatFileSize(row.size_bytes)}
                    </td>
                    <td className="py-2 px-3 text-zephyr-muted">
                      {row.created_at ? String(row.created_at).slice(0, 10) : ""}
                    </td>
                    <td className="py-2 px-3">
                      <button type="button" onClick={() => copyLink(row)} className={linkBtnCls}>
                        {copiedId === row.id ? "Copied ✓" : "Copy link"}
                      </button>
                      <button type="button" onClick={() => startRename(row)} className={linkBtnCls}>
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={() => pickReplacement(row)}
                        title="Upload a new version — the link stays the same"
                        className={linkBtnCls}
                      >
                        Replace file
                      </button>
                      <button
                        type="button"
                        onClick={() => removeDocument(row)}
                        className="bg-transparent border-none cursor-pointer text-[12px] text-[#C62828] px-1"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
